import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { FaSave, FaTimes, FaSpinner, FaTrash, FaPrint } from "react-icons/fa";
import dayjs from 'dayjs';
import Select from "react-select";

interface OutboundOrderDetail {
  id?: string;
  productId: string;
  qty_requested: number;
  qty_picked?: number;
}

interface OutboundOrder {
  id?: string;
  outbound_order_no?: string;
  so_number?: string;
  customer_id: string;
  warehouse_id: string;
  status?: string;
  created_by?: string;
  created_at?: string;
  details: OutboundOrderDetail[];
}

interface Product {
  id: string;
  name: string;
}

const OutboundOrdersForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isEdit = Boolean(id);
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState<OutboundOrder>({
    customer_id: '',
    warehouse_id: '',
    details: [],
  });
  
  
  const [orderDetails, setOrderDetails] = useState<OutboundOrderDetail[]>([
    {
	  productId: "",
	  qty_requested: 0,
	  qty_picked: 0,
    },
  ]);


  const [customers, setCustomers] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  const isReadOnly = ['Submitted', 'Approved', 'Rejected'].includes(formData.status || '');

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    };
  };

  // Tambah state untuk simpan data outbound order full
  const [outboundOrderData, setOutboundOrderData] = useState<any>(null);

  // Di fetchData, setelah fetch outbound order, simpan data ke outboundOrderData
  const fetchData = async () => {
	  try {
		const [customerRes, warehouseRes, productRes] = await Promise.all([
		  axios.get(`${API}/customers/`, getAuthHeader()),
		  axios.get(`${API}/warehouses/`, getAuthHeader()),
		  axios.get(`${API}/products/`, getAuthHeader()),
		]);
		setCustomers(Array.isArray(customerRes.data.data) ? customerRes.data.data : []);
		setWarehouses(Array.isArray(warehouseRes.data.data) ? warehouseRes.data.data : []);
		setProducts(Array.isArray(productRes.data.data) ? productRes.data.data : []);

		if (isEdit) {
		  const res = await axios.get(`${API}/outbound-orders/${id}`, getAuthHeader());
		  const data = res.data;

		  setFormData({
			id: data.id,
			outbound_order_no: data.outboundOrderNo || '',
			so_number: data.soNumber || '',
			customer_id: data.customer?.id || data.customerId || '',
			warehouse_id: data.warehouse?.id || data.warehouseId || '',
			status: data.status || 'Draft',
			created_by: data.createdByUser?.name || '',
			created_at: data.createdAt || '',
			details: data.details.map((d: any) => ({
			  id: d.id,
			  productId: d.product?.id || '',
			  qty_requested: d.qty_requested ?? 0,
			  qty_picked: d.qty_picked ?? 0,
			})),
		  });
		}
	  } catch (error: any) {
		if (error.response?.status === 401) {
		  logout();
		  navigate('/login');
		}
	  } finally {
		setLoading(false);
	  }
	};

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (
    index: number,
    field: keyof OutboundOrderDetail,
    value: string | number
  ) => {
    setFormData(prev => {
      const newDetails = [...prev.details];
      newDetails[index] = {
        ...newDetails[index],
        [field]: (field === 'qty_requested' || field === 'qty_picked') ? Number(value) : value,
      };
      return { ...prev, details: newDetails };
    });
  };

  const addDetailRow = () => {
    setFormData(prev => ({
      ...prev,
      details: [...prev.details, { productId: '', qty_requested: 0 }],
    }));
  };

  const removeDetailRow = (index: number) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  //submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

	setErrorMessage(null); 
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    const validDetails = formData.details.filter(
      (d) => d.productId && d.qty_requested > 0
    );

    if (validDetails.length === 0) {
      alert("Please add at least one valid product with qty requested.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    try {
      const payload = {
		  customerId: formData.customer_id,
		  warehouseId: formData.warehouse_id,
		  soNumber: formData.so_number,
		  details: validDetails.map((d) => ({
			id: d.id,
			productId: d.productId,
			qty_requested: d.qty_requested || 0,
			qty_picked: d.qty_picked || 0,
		  })),
		};


      if (isEdit) {
	    //console.log("Payload submitted:", payload);
        await axios.put(`${API}/outbound-orders/${id}`, payload, getAuthHeader());
        alert("Outbound Order updated!");
      } else {
		//console.log("Payload being sent to API:", payload);
        await axios.post(`${API}/outbound-orders`, payload, getAuthHeader());
        alert("Outbound Order created!");
      }

      navigate("/outbound-orders");
    } catch (error : any) {
      //console.error("Failed to save inbound order:", error);
	  if (error.response && error.response.data && error.response.data.message) {
		  setErrorMessage(error.response.data.message);
		} else {
		  setErrorMessage("Failed to save. Please try again.");
		}
      //alert("Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  
  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this data?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/outbound-orders/${id}`, getAuthHeader());
      alert("Outbound Order deleted");
      navigate("/outbound-orders");
    } catch (err) {
      //console.error("Delete error:", err);
      alert("Failed to delete Outbound Order");
    }
  };

  const changeStatus = async (status: 'submit' | 'approve' | 'reject') => {
    try {
      await axios.patch(`${API}/outbound-orders/${id}/${status}`, null, getAuthHeader());
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleApprove = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    try {
		await axios.patch(`${API}/outbound-orders/${formData.id}/approve`, {}, getAuthHeader());
		alert("Outbound Order Approved!");
		navigate("/outbound-orders");
	  } catch (error: any) {
		if (error.response?.data?.message) {
		  setErrorMessage(error.response.data.message);
		} else {
		  setErrorMessage("Failed to approve. Please try again.");
		}
	  } finally {
		setLoading(false);
	  }
  };

  const handleReject = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/outbound-orders/${formData.id}/reject`, {}, getAuthHeader());
      navigate("/outbound-orders");
	alert("Outbound Order Rejected!");
	setLoading(false);

  };
  
  const handleSubmitStatus = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!window.confirm("Do you want to save this data?")) return;

    setLoading(true);
    await axios.patch(`${API}/outbound-orders/${formData.id}/submit`, {}, getAuthHeader());
      navigate("/outbound-orders");
	alert("Outbound Order Submitted!");
	setLoading(false);
  };
  
  const handlePrint = async (): Promise<void> => {
	  const printWindow = window.open("", "_blank");

	  if (!printWindow) {
		alert("Popup blocked! Izinkan pop-up untuk mencetak.");
		return;
	  }

	  const formatDate = (value?: string) =>
		value ? dayjs(value).format("DD/MM/YYYY") : "-";

	  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

	  //const locationMap = Object.fromEntries(locations.map((l) => [l.id, l.name]));

	  // 🔁 Fetch default location name per product
	  //const defaultLocationMap: Record<string, string> = {};
	  for (const item of formData.details) {
		try {
		  const res = await axios.get(`${API}/products/${item.productId}`, getAuthHeader());
		  const product = res.data;
		  //defaultLocationMap[item.productId] = product?.location?.name || "-";
		} catch (error) {
		  //defaultLocationMap[item.productId] = "-";
		}
	  }
	  const html = ``;

	  printWindow.document.open();
	  printWindow.document.write(html);
	  printWindow.document.close();
	};

  const isDisabled = formData.status === "Approved" || formData.status === "Rejected" || formData.status === "Submitted";
  
  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? "Edit Outbound Order" : "Add Outbound Order"}
          </h2>

          <div className="flex gap-2">
		    {formData.status === "Draft" && (
			  <button
				onClick={handleSubmitStatus}
				type="button"
				className="bg-yellow-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
			  >
				Submit
			  </button>
			)}
			
			{formData.status === "Approved" && (
			  <button
				type="button"
				onClick={handlePrint}
				className="bg-indigo-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
			  >
				<FaPrint /> Print Putaway Form
			  </button>
			)}

			{formData.status === "Submitted" &&  (
			  <>
				<button
				  onClick={handleApprove}
				  type="button"
				  className="bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
				>
				  Approve
				</button>
				<button
				  onClick={handleReject}
				  type="button"
				  className="bg-red-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
				>
				  Reject
				</button>
			  </>
			)}
			{formData.status !== "Approved" && formData.status !== "Rejected" && formData.status !== "Submitted" && (
            <button
              type="submit"
              form="outbound-form"
              className="bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 text-[13px]"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />} Save
            </button>
			)}
            <button
              onClick={() => navigate("/outbound-orders")}
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded flex items-center gap-1 text-[13px]"
            >
              <FaTimes /> Cancel
            </button>
			{formData.status === "Draft" && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-2 text-[13px] hover:bg-red-700 disabled:opacity-50"
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

		{errorMessage && (
		  <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-400 rounded">
			{errorMessage}
		  </div>
		)}

        <form id="outbound-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">Outbound Order No</label>
                <input
                  type="text"
                  name="outbound_order_no"
                  value={formData.outbound_order_no}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  required
				  disabled
                />
            </div>
			<div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Customer <span>*</span></label>
              <select
                name="customer_id"
                value={formData.customer_id || ''}
                onChange={handleChange}
                disabled={isReadOnly}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
				required
              >
                <option value="">-- Select Customer --</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Warehouse <span>*</span></label>
              <select
                name="warehouse_id"
                value={formData.warehouse_id || ''}
                onChange={handleChange}
                disabled={isReadOnly}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
				required
              >
                <option value="">-- Select Warehouse --</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="w-36 text-[13px] font-medium">Sales Ord No</label>
              <input
                name="so_number"
                value={formData.so_number || ''}
                onChange={handleChange}
                disabled={isReadOnly}
                className="flex-1 border rounded px-2 py-1 text-[13px]"
              />
            </div>

            {[
              { label: "Status", value: formData.status },
              { label: "Created By", value: formData.created_by },
              {
                label: "Created At",
                value: formData.created_at
                  ? dayjs(formData.created_at).format("DD/MM/YYYY HH:mm")
                  : "",
              },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <label className="w-36 text-[13px] font-medium">{item.label}</label>
                <input
                  type="text"
                  value={item.value || ""}
                  disabled
                  className="flex-1 border rounded px-2 py-1 text-[13px] bg-gray-100"
                />
              </div>
            ))}
          </div>

		  <div className="mt-6">
            <h3 className="font-semibold mb-2">Outbound Order Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px] border">
				  <thead className="bg-gray-100">
					<tr>
					  <th className="border px-2 py-2 w-10">#</th>
					  <th className="border px-2 py-1">Product</th>
					  <th className="border px-2 py-1">Qty Requested</th>
					  <th className="border px-2 py-1">Qty Picked</th>
					  <th className="border px-2 py-1 text-center w-10">Action</th>
					</tr>
				  </thead>
				  <tbody>
					{formData.details.map((detail, index) => (
					  <tr key={index}>
						<td className="border px-2 py-1 text-center">{index + 1}</td>
						<td className="border px-2 py-1">
							<Select
							  className="text-[13px]"
							  options={products.map((prod) => ({
								value: prod.id,
								label: prod.name,
							  }))}
							  value={products
								.map((prod) => ({
								  value: prod.id,
								  label: prod.name,
								}))
								.find((opt) => opt.value === detail.productId)}
							  onChange={(selected) =>
								handleDetailChange(index, "productId", selected?.value || "")
							  }
							  isDisabled={isDisabled}
							  menuPortalTarget={document.body}
							  menuPosition="fixed"
							  styles={{
								menuPortal: (base) => ({ ...base, zIndex: 9999 }),
								control: (base) => ({ ...base, fontSize: "13px" }),   // ukuran font di input
								option: (base) => ({ ...base, fontSize: "13px" }),    // ukuran font di dropdown
								singleValue: (base) => ({ ...base, fontSize: "13px" }), // ukuran font saat item terpilih
							  }}
	/>
						  </td>
						<td className="border px-2 py-1">
						  <input
							type="number"
							value={detail.qty_requested || 0}
							onChange={(e) => handleDetailChange(index, 'qty_requested', e.target.value)}
							disabled={isReadOnly}
							min={0}
							className="w-full border rounded px-2 py-1 text-right"
						  />
						</td>
						<td className="border px-2 py-1">
						  <input
							type="number"
							value={detail.qty_picked || 0}
							onChange={(e) => handleDetailChange(index, 'qty_picked', e.target.value)}
							disabled={true}
							min={0}
							className="w-full border rounded px-2 py-1 text-right"
						  />
						</td>
						<td className="border px-2 py-1 text-center">
						  {!isReadOnly && (
							<button
							  type="button"
							  onClick={() => removeDetailRow(index)}
							  className="text-red-600 hover:text-red-800"
							  title="Remove"
							>
							  <FaTrash />
							</button>
						  )}
						</td>
					  </tr>
					))}
				  </tbody>
			  </table>
			</div>
			{!isDisabled && (<button
              type="button"
              onClick={addDetailRow}
              className="mt-2 bg-green-600 text-white px-3 py-1 rounded text-[13px]"
            >
              + Add Product
            </button>)}
          </div>
        </form>
      </div>
    </div>
  );
};

export default OutboundOrdersForm;
